/**
 * Funções puras de data usadas pelo componente DateInputBR.
 * Extraídas para permitir testes unitários sem renderizar o componente.
 *
 * Convenção: o "valor" canônico é sempre ISO `YYYY-MM-DD`; a exibição é pt-BR
 * `DD/MM/AAAA`. Nenhuma conversão de fuso horário é feita (datas de calendário).
 */

export const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export const WEEKDAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Converte ISO `YYYY-MM-DD` em `DD/MM/AAAA` (string vazia se inválido/vazio). */
export const isoToBR = (iso: string): string => {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
};

/** Converte `DD/MM/AAAA` em ISO `YYYY-MM-DD`, validando a data real. '' se inválido. */
export const brToISO = (br: string): string => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br.trim());
  if (!match) return '';
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12) return '';
  if (day < 1 || day > 31) return '';
  if (year < 1900 || year > 9999) return '';
  // Valida data real (ex: 31/02 inválido).
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return '';
  return `${match[3]}-${match[2]}-${match[1]}`;
};

/** Aplica a máscara DD/MM/AAAA conforme o usuário digita (apenas dígitos). */
export const applyMask = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/** Quebra um ISO YYYY-MM-DD em {year, month(0-11), day}, ou null. */
export const parseISO = (iso: string): { year: number; month: number; day: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  return { year: parseInt(match[1], 10), month: parseInt(match[2], 10) - 1, day: parseInt(match[3], 10) };
};

/** Monta o ISO de um dia do mês/ano em exibição. */
export const isoForDay = (year: number, month: number, day: number): string =>
  `${year}-${pad2(month + 1)}-${pad2(day)}`;

/**
 * Monta a grade do mês para exibição no calendário: células `null` no início para
 * alinhar o primeiro dia ao dia da semana correto, seguidas dos números dos dias.
 */
export const buildMonthGrid = (year: number, month: number): (number | null)[] => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
};

/** Indica se um dia (ISO) está fora do intervalo permitido [min, max]. */
export const isDayDisabled = (iso: string, min?: string, max?: string): boolean => {
  if (min && iso < min) return true;
  if (max && iso > max) return true;
  return false;
};
